import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Wallet, Pencil, Archive, Coins, Landmark, Mail, Banknote } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = { cash: "Contanti", bank: "Banca", postal: "Posta", paypal: "PayPal", stripe: "Stripe", other: "Altro" };
const KIND_ICON = { cash: Coins, bank: Landmark, postal: Mail, paypal: Banknote, stripe: Banknote, other: Wallet };

function euro(n) { return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0); }

export default function Casse() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const { data: accounts = [] } = useQuery({
    queryKey: ["admin-accounts-full"],
    queryFn: async () => (await api.get("/administration/accounts", { params: { include_archived: true } })).data,
  });

  const archiveMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/administration/accounts/${id}`)).data,
    onSuccess: () => { toast.success("Cassa aggiornata"); qc.invalidateQueries({ queryKey: ["admin-accounts-full"] }); qc.invalidateQueries({ queryKey: ["admin-accounts"] }); },
  });

  return (
    <div className="p-6 max-w-[1200px] mx-auto space-y-5" data-testid="casse-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1C352D]">Casse e Conti</h1>
          <p className="text-sm text-stone-500 mt-1">Conti bancari, carte, portafogli digitali e cassa contante.</p>
        </div>
        <Button onClick={() => { setEditing({ name: "", kind: "bank", color: "#1C352D", opening_balance: 0 }); setOpen(true); }} data-testid="new-account-btn" className="bg-[#1C352D] hover:bg-[#264A3E]"><Plus size={14} className="mr-1"/> Nuova cassa</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {accounts.map((a) => {
          const Icon = KIND_ICON[a.kind] || Wallet;
          return (
            <Card key={a.id} className={`p-5 border border-stone-200 rounded-xl shadow-none ${a.archived ? "opacity-50" : ""}`} data-testid={`account-card-${a.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 rounded-lg grid place-items-center text-white shrink-0" style={{ background: a.color || "#1C352D" }}>
                  <Icon size={18}/>
                </div>
                <div className="flex items-center gap-1">
                  {a.is_preset && <Badge className="bg-stone-100 text-stone-600 text-[9px] rounded-full border border-stone-200">preset</Badge>}
                  {a.archived && <Badge className="bg-amber-100 text-amber-800 text-[9px] rounded-full border border-amber-200">archiviato</Badge>}
                </div>
              </div>
              <div className="font-semibold text-lg text-[#1C352D]">{a.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500">{KIND_LABEL[a.kind]}</div>
              <div className="mt-3 text-2xl font-bold" style={{ color: a.balance >= 0 ? "#1C352D" : "#dc2626" }}>{euro(a.balance)}</div>
              <div className="mt-1 text-[11px] text-stone-500 flex gap-3">
                <span>Entrate: <b className="text-emerald-700">{euro(a.total_in)}</b></span>
                <span>Uscite: <b className="text-red-700">{euro(a.total_out)}</b></span>
              </div>
              <div className="mt-3 flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => { setEditing(a); setOpen(true); }} data-testid={`edit-account-${a.id}`}><Pencil size={12} className="mr-1"/> Modifica</Button>
                {!a.archived && <Button size="sm" variant="ghost" className="text-stone-500 hover:text-red-700" onClick={() => archiveMut.mutate(a.id)} data-testid={`archive-account-${a.id}`}><Archive size={12} className="mr-1"/> Archivia</Button>}
              </div>
            </Card>
          );
        })}
      </div>

      {open && <AccountDialog account={editing} onClose={() => { setOpen(false); setEditing(null); }} onSaved={() => { qc.invalidateQueries({ queryKey: ["admin-accounts-full"] }); qc.invalidateQueries({ queryKey: ["admin-accounts"] }); qc.invalidateQueries({ queryKey: ["admin-summary"] }); }}/>}
    </div>
  );
}

function AccountDialog({ account, onClose, onSaved }) {
  const [form, setForm] = useState(account);
  const [saving, setSaving] = useState(false);
  const isEdit = !!account?.id;

  const save = async () => {
    if (!form.name) { toast.error("Il nome è obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, kind: form.kind, color: form.color, opening_balance: parseFloat(form.opening_balance) || 0, notes: form.notes };
      if (isEdit) await api.patch(`/administration/accounts/${account.id}`, payload);
      else await api.post(`/administration/accounts`, payload);
      toast.success(isEdit ? "Cassa aggiornata" : "Cassa creata");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{isEdit ? "Modifica cassa" : "Nuova cassa"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Nome *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="acc-name"/></div>
          <div>
            <Label>Tipo</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                {Object.entries(KIND_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Saldo iniziale (€)</Label><Input type="number" step="0.01" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} className="mt-1"/></div>
            <div><Label>Colore</Label><Input type="color" value={form.color || "#1C352D"} onChange={(e) => setForm({ ...form, color: e.target.value })} className="mt-1 h-10"/></div>
          </div>
          <div><Label>Note</Label><Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="IBAN, intestatario, ecc." className="mt-1"/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="acc-save" className="bg-[#1C352D] hover:bg-[#264A3E]">{saving ? "…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
